import React, { useState } from "react";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import PasswordRules from "@src/components/forms/PasswordRules";
import { useFormContext } from "react-hook-form";
import { COMMON_CONTROLS } from "../../stories/utils/common-controls";

const FormInput = ({
  textColor,
  className = "",
  label,
  info,
  id,
  error,
  name,
  type,
  showPasswordRules,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  onChange,
  validationMode,
  fValue,
  autoComplete,
  minDate,
  maxDate,
}) => {
  const {
    register,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [pValue, setPValue] = useState("");

  const { ref, onChange: rhfOnChange, ...registerProps } = register(name);
  const handleChange = async (event) => {
    rhfOnChange(event); // rhf --> react-hook-form ?
    if (onChange) {
      await onChange(event);
    }
    if (validationMode === "onChange") {
      await trigger(name);
    }
  };

  const handlePassword = async (e) => {
    rhfOnChange(e);
    setPValue(e.target.value);
    if (validationMode === "onChange") {
      await trigger(name);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id ? id : name}
          className={`block text-sm font-bold text-${textColor ? `${textColor}-500` : "gray-700"} mb-2`}
        >
          {label}
          {required && <span>{" * "}</span>}
        </label>
      )}
      {info && (
        <label
          className={`block tracking-wide text-gray-400 text-xs text-left mb-2`}
          htmlFor={id ? id : name}
        >
          {info}
        </label>
      )}
      <input
        {...registerProps}
        id={id ? id : name}
        ref={ref}
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete={autoComplete}
        min={type === "date" ? minDate : undefined}
        max={type === "date" ? maxDate : undefined}
        onChange={
          type === "password"
            ? handlePassword
            : validationMode === "onChange"
              ? handleChange
              : onChange
        }
        value={fValue}
        className={`text-sm appearance-none focus:outline-none block w-full  placeholder-gray-400 border rounded-lg py-3 px-4 leading-tight  ${
          !!error
            ? " border-error focus:border-error"
            : " border-gray-200 focus:border-primary-500"
        } ${!error & !disabled ? " hover:border-gray-400" : ""} ${
          // allow hotjar to capture keystrokes unless its a password
          type !== "password" ? "data-hj-allow" : ""
        } ${!!disabled ? "bg-primary-100 text-gray-400" : " bg-white text-gray-700"}  
        ${className} `}
      />
      {(error || errors[name]?.message) && (
        <p className="mt-1 text-xs text-error">
          {error || errors[name]?.message}
        </p>
      )}
      {type === "password" && !!showPasswordRules && (
        <PasswordRules value={pValue} error={error} />
      )}
    </div>
  );
};

FormInput.displayName = "Input";

export const formInputControls = {
  name: {
    propType: PropTypes.string.isRequired,
    description: "Input name attribute",
    controlType: "text",
    defaultValue: "input-name",
  },
  type: {
    propType: PropTypes.string,
    description: "Input type",
    controlType: "select",
    options: ["text", "password", "email", "number", "tel", "url"],
    defaultValue: "text",
  },
  textColor: COMMON_CONTROLS.colour_name_shaded,
};

FormInput.propTypes = AppUtils.getPropTypeFromControls(formInputControls);

export default FormInput;
